import { z } from 'zod';
/**
 * The aiconcierge **partner provisioning API**, as this repo understands it.
 *
 * The document is `../aiconcierge/docs/partner-provisioning-api.md` and it is
 * *theirs*: they issue it, they implement it, and this file is a reader of it
 * exactly the way `packages/contracts/src/wire.ts` is a reader of the provider
 * agreement — except that the arrow points the other way. There we serve and
 * they consume; here they serve and we consume.
 *
 * Two rules shape every schema below.
 *
 * **1. Tolerant on the way in, strict on the way out.** Their responses are
 * parsed with ordinary zod objects, which *strip* unknown keys: a field they
 * add next month does not break a console screen, which is the same
 * degradation rule the provider agreement's §5.1.1 tiers give them. The one
 * schema that is `.strict()` is `AgentSettingsPatch` — the body we *send* —
 * because §1 of their document ("yourstore owns what the business is,
 * aiconcierge owns how the agent behaves") is only true if our proxy refuses to
 * forward a field that is not the agent's. Their `PATCH` answers `422` to an
 * unknown field; we would rather never have asked.
 *
 * **2. Nothing in here is ever logged.** `EnableRequest.provider.api_key` and
 * `.webhook_secret` are live credentials that cross this process once, on their
 * way from `packages/store` to their Secret Manager. They are named here so the
 * services' pino redaction lists can name the same paths — see
 * `CONCIERGE_REDACT_FIELDS` at the foot of this file.
 */
/* ------------------------------------------------------------------ */
/* Their error envelope (§2)                                           */
/* ------------------------------------------------------------------ */
/**
 * `unauthorized 401 · not_found 404 · conflict 409 · invalid 422 ·
 * provider_error 502` — their closed set, which is *not* ours. `client.ts` maps
 * it onto `@yourstore/domain`'s `ErrorCode`; this schema is only what arrives.
 */
export const PartnerErrorCode = z.enum([
    'unauthorized',
    'not_found',
    'conflict',
    'invalid',
    'provider_error',
]);
export const PartnerErrorBody = z.object({
    error: z.object({
        /** Their vocabulary, kept as a string: an unrecognised code is still an error. */
        code: z.string(),
        message: z.string(),
        /**
         * Free-form. For a `422` on `PATCH …/settings` this is where the field-level
         * complaint lives, and the panel renders it beside the field — which is why
         * it is carried verbatim rather than flattened into a sentence.
         */
        detail: z.unknown().optional(),
    }),
});
/* ------------------------------------------------------------------ */
/* The response every endpoint returns (§3)                            */
/* ------------------------------------------------------------------ */
/** `connected` when our key works from their side; anything else is a to-do. */
export const ProviderStatus = z.enum(['connected', 'expired', 'error', 'unknown']);
export const ProviderConnection = z.object({
    adapter: z.string(),
    /**
     * `.catch('unknown')` rather than a hard parse. A status word we have not seen
     * is a console that should say "we do not recognise this" — not a screen that
     * fails to render, at the exact moment somebody is trying to find out why the
     * concierge is quiet.
     */
    status: ProviderStatus.catch('unknown'),
    detail: z.string().nullish(),
    capabilities: z.record(z.string(), z.unknown()).nullish(),
});
export const SyncSource = z.object({
    adapter: z.string(),
    version: z.number().nullish(),
    synced_at: z.string().nullish(),
});
/** The agent's tone. `formality` is `casual · warm · professional` (§5). */
export const AgentTone = z.object({
    voice: z.string().nullish(),
    formality: z.string().nullish(),
    avoid: z.array(z.string()).nullish(),
});
export const AgentGuardrails = z.object({
    price_floor_cents: z.number().nullish(),
    min_lead_time_hours: z.number().nullish(),
    max_party_size: z.number().nullish(),
    deposit_required_above_cents: z.number().nullish(),
    never_promise: z.array(z.string()).nullish(),
});
/**
 * One row of the "the AI may book this" switch. `id` is a **provider service
 * id** — ours — which is what makes the join against the owner's own catalog
 * exact rather than by name (the incident behind the whole handoff was a
 * hand-copied catalog).
 */
export const AgentService = z.object({
    id: z.string(),
    bookable_by_ai: z.boolean(),
    /** Their copy of our name, if they send one. The owner's own list wins on screen. */
    name: z.string().nullish(),
});
export const AgentSettings = z.object({
    autonomy: z.string().nullish(),
    autonomous_value_ceiling_cents: z.number().nullish(),
    greeting: z.string().nullish(),
    voice_greeting: z.string().nullish(),
    tone: AgentTone.nullish(),
    voice_mode: z.string().nullish(),
    owner_number: z.string().nullish(),
    guardrails: AgentGuardrails.nullish(),
    services: z.array(AgentService).nullish(),
});
/**
 * §3's response, returned by every endpoint of the API.
 *
 * `site_key` is the **public** chat-widget key for the tenant's site — it is not
 * a secret, it is the thing a `<script>` tag carries, and it is the one field
 * here we persist (`tenant_config.config.concierge.site_key`).
 */
export const PartnerTenant = z.object({
    tenant_id: z.string(),
    status: z.string(),
    created: z.boolean().nullish(),
    provider: ProviderConnection.nullish(),
    catalog_source: SyncSource.nullish(),
    profile_source: SyncSource.nullish(),
    sync: z.record(z.string(), z.unknown()).nullish(),
    settings: AgentSettings.nullish(),
    config_version: z.number().nullish(),
    site_key: z.string().nullish(),
    webhook_url: z.string().nullish(),
    console_url: z.string().nullish(),
    missing: z.array(z.string()).nullish(),
});
/**
 * **The agent-owned fields, mirrored from their §5 table — and this list is a
 * boundary, not a convenience.**
 *
 * Our proxy validates an owner's `PATCH` against this schema before forwarding
 * it, so `hours`, `prices`, `policies` and `faq` cannot be sent to the
 * concierge "for the concierge". Those are edited where they already are, in
 * yourstore, and reach the agent through `GET /v1/profile` and the
 * `profile.updated` webhook. One fact, one editor.
 *
 * `.strict()` makes an unknown key our `400` rather than their `422`: the same
 * refusal, one hop earlier, with our own sentence on it.
 *
 * Every field is optional because the `PATCH` is partial — **only changed fields
 * are sent** — and `null` is meaningful on several of them (`null` clears the
 * ceiling, the owner's number, the voice greeting). `.nullable()` where their
 * table says `null` clears it; not where it does not.
 */
export const AgentSettingsPatch = z
    .object({
    autonomy: z.enum(['conservative', 'balanced', 'autonomous']).optional(),
    autonomous_value_ceiling_cents: z.number().int().min(0).nullable().optional(),
    greeting: z.string().max(2000).nullable().optional(),
    voice_greeting: z.string().max(2000).nullable().optional(),
    tone: z
        .object({
        voice: z.string().max(2000).nullable().optional(),
        formality: z.enum(['casual', 'warm', 'professional']).optional(),
        avoid: z.array(z.string().max(200)).max(50).optional(),
    })
        .strict()
        .optional(),
    voice_mode: z.enum(['disabled', 'ai_first', 'owner_first']).optional(),
    /** E.164, checked here so a typo is refused by the field rather than by them. */
    owner_number: z
        .string()
        .regex(/^\+[1-9]\d{6,14}$/, 'must be E.164, like +13405550100')
        .nullable()
        .optional(),
    guardrails: z
        .object({
        price_floor_cents: z.number().int().min(0).nullable().optional(),
        min_lead_time_hours: z.number().int().min(0).max(8760).nullable().optional(),
        max_party_size: z.number().int().min(1).max(1000).nullable().optional(),
        deposit_required_above_cents: z.number().int().min(0).nullable().optional(),
        never_promise: z.array(z.string().max(200)).max(50).optional(),
    })
        .strict()
        .optional(),
    services: z
        .array(z.object({ id: z.string().min(1), bookable_by_ai: z.boolean() }).strict())
        .max(500)
        .optional(),
})
    .strict();
/** The whitelist as a list, so a refusal can name what *is* allowed. */
export const AGENT_SETTING_FIELDS = [
    'autonomy',
    'autonomous_value_ceiling_cents',
    'greeting',
    'voice_greeting',
    'tone',
    'voice_mode',
    'owner_number',
    'guardrails',
    'services',
];
/** `POST /tenants/{id}/status` (§6). Paused is capture mode; nothing is deleted. */
export const PartnerStatus = z.enum(['enabled', 'paused']);
/**
 * The three plain-language autonomy meanings, from their §5 table, so that both
 * consoles render the same sentences and neither invents its own.
 *
 * They are English here and translated in each app's message catalog — the
 * *keys* are the contract. The token (`balanced`) is never translated, for the
 * reason `apps/admin`'s catalog gives about module slugs: an operator and an
 * owner reading two vocabularies for one system is worse than an English word.
 */
export const AUTONOMY_LEVELS = ['conservative', 'balanced', 'autonomous'];
export const VOICE_MODES = ['disabled', 'ai_first', 'owner_first'];
export const FORMALITIES = ['casual', 'warm', 'professional'];
/**
 * The field names that must never reach a log line, in the two spellings a
 * `logger.child({ … })` would land them under.
 *
 * `apps/admin-api/src/logging.ts` and `apps/core-api/src/logging.ts` both splice
 * this into their redaction paths, so adding a credential-carrying field to the
 * wire above is a change in *one* place rather than three.
 */
export const CONCIERGE_REDACT_FIELDS = [
    'webhook_secret',
    'webhookSecret',
    'platform_key',
    'platformKey',
    'partner_key',
    'partnerKey',
    'provider',
];
//# sourceMappingURL=wire.js.map